import type { Distance, Margins, SpatialRectangle } from "@pdfking/math"
import { AddMargins, RemoveMargins, Scale, Subtract, Total } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"

import { PALETTE, PUBLISHER, TITLE } from "#constants"
import type { Measure } from "#internal"

import { DrawPlate } from "../layout/DrawPlate"
import { PageMetrics } from "../layout/PageMetrics"
import { PosterBox } from "../layout/PosterBox"
import { DrawPoster } from "../poster/DrawPoster"
import { CreateRandom } from "../random/CreateRandom"
import { AuthorName } from "../text/AuthorName"
import { PosterWord } from "../text/PosterWord"

// The front of the cover in `box`: the primary, title on top, inked in the
// spot too, a poster on a plate centered in the page, the author under it and
// the publisher at the foot, the text within `security`; the primary runs on
// into `bleed`
export const DrawFrontCover = ({
  bleed,
  box,
  measure,
  security,
  seed,
}: {
  bleed: Margins<Distance>
  box: SpatialRectangle<Distance>
  measure: Measure
  security: Margins<Distance>
  seed: StringGUID
}): PageShape[] => {
  const { font_size, short } = PageMetrics(box)
  const frame = RemoveMargins(box, security)
  const plate = PosterBox(frame, box)
  const title_size = Scale(short, 0.1)
  const author_size = Scale(font_size, 1.4)
  const publisher_size = Scale(font_size, 0.8)
  const title_top = Total(frame.y, frame.height)
  return [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: AddMargins(box, bleed) },
    ...[PALETTE.text, PALETTE.spot].map((text_color): PageShape => ({
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(title_size),
      horizontal_align: HorizontalAlign.CENTER,
      position: { ...frame, height: title_size, y: Subtract(title_top, title_size) },
      text: TITLE,
      text_color,
      vertical_align: VerticalAlign.TOP,
    })),
    ...DrawPlate({
      box: plate,
      draw: inside =>
        DrawPoster({
          box: inside,
          measure,
          panels: 1,
          random: CreateRandom(seed, "front cover"),
          word: PosterWord(seed, 0),
          word_panel: 0,
        }),
      short,
    }),
    {
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(author_size),
      horizontal_align: HorizontalAlign.CENTER,
      position: {
        ...frame,
        height: author_size,
        y: Subtract(plate.y, Total(Scale(short, 0.03), author_size)),
      },
      text: AuthorName(seed),
      text_color: PALETTE.text,
      vertical_align: VerticalAlign.TOP,
    },
    {
      enum: PlotFunction.PLOT_TEXT,
      font_size: ToPixel(publisher_size),
      horizontal_align: HorizontalAlign.CENTER,
      position: { ...frame, height: publisher_size },
      text: PUBLISHER,
      text_color: PALETTE.text,
      vertical_align: VerticalAlign.TOP,
    },
  ]
}
